import React from "react";
import {
  Stack,
  Checkbox,
  DefaultButton,
  Callout,
  DirectionalHint,
} from "office-ui-fabric-react";
import { IAirCooledChillerReading, IReadingStatus } from "../../../vm";
import {
  ODD_SHIFT,
  UPS_STATUS,
  READ_AT_TIME_FORMAT,
  childrenGap8,
  EVEN_SHIFT,
} from "../../common/Constants";
import moment from "moment";
import Icon from "@mdi/react";
import { mdiDotsVertical } from "@mdi/js";
import ManageAccDialog from "./ManageAccDialog";
import { ToastContext } from "../../common/ToastProvider";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import { updateAccReadingStatus } from "../../../services/infrastructure/AccService";
import {
  isInThreshold,
  isValueLessThanThreshold,
} from "../../../services/ThresholdService";
import ManageAllRejectDialog from "../../common/ManageAllRejectDialog";
import ShowThresholdIcon from "../../common/ShowThresholdIcon";

export interface AccTableProps {
  accReading: IAirCooledChillerReading[];
  threshold: any;
}

const AccTable: React.FC<AccTableProps> = ({ accReading, threshold }) => {
  //state
  const [state, setState] = React.useState({
    isLoading: false,
    manageAccDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    accReading: [],
    isAllChecked: false,
    selectedItems: [],
    manageRejectDialog: {
      isOpen: false,
      ids: [],
      remarks: "",
    },
    manageActionsCallout: {
      isOpen: false,
      element: undefined,
      data: undefined,
    },
  } as {
    isLoading: boolean;
    manageAccDialog: {
      isOpen: boolean;
      index?: number;
      data?: IAirCooledChillerReading;
    };
    accReading: IAirCooledChillerReading[];
    isAllChecked: boolean;
    selectedItems: string[];
    manageRejectDialog: {
      isOpen: boolean;
      ids: string[];
      remarks?: string;
    };
    manageActionsCallout: {
      isOpen: boolean;
      element: any;
      data?: IAirCooledChillerReading;
    };
  });

  React.useEffect(() => {
    setState({ ...state, accReading: [...accReading] });
  }, [accReading]);

  //hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  const handleAcceptOrRejectSelected = (isAccept: boolean) => {
    if (isAccept) {
      showConfirmDialog(
        "Are You Sure",
        `Do you want to ${
          isAccept ? "Accept" : "Reject"
        } selected air cooled chiller reading`,
        async () => {
          let selectedItems = [...state.selectedItems];
          let accReading = [...state.accReading];
          setState({ ...state, isLoading: true });
          let obj: IReadingStatus = {
            ids: selectedItems,
            status: isAccept ? "approved" : "rejected",
          };
          let result = await updateAccReadingStatus(obj);
          if (result?.success) {
            selectedItems.forEach((item) => {
              let index = accReading.findIndex((ele) => ele._id === item);
              if (index !== -1) {
                accReading[index].status = isAccept ? "approved" : "rejected";
              }
            });
            showToast(
              `Air Cooled Chiller Reading ${
                isAccept ? "approved" : "rejected"
              } successfully`,
              "success"
            );
            setState({
              ...state,
              accReading,
              selectedItems: [],
              isLoading: false,
            });
          } else {
            showToast(
              result?.message ||
                `An error occurred while ${
                  isAccept ? "approving" : "rejecting"
                } a air cooled chiller reading`,
              "error"
            );
            setState({
              ...state,
              isLoading: false,
            });
          }
        }
      );
    } else {
      let selectedItems = [...state.selectedItems];
      handleRejectAllOpenDialog(selectedItems);
    }
  };

  const onSelectAll = (isAllChecked: boolean) => {
    let selectedItems: string[] = [];
    if (isAllChecked) {
      state.accReading.forEach((ele: any) => {
        if (ele.status === "submitted") {
          selectedItems.push(ele._id as string);
        }
      });
      setState({ ...state, isAllChecked: true, selectedItems });
    } else {
      setState({ ...state, isAllChecked: false, selectedItems });
    }
  };

  const onIndividualSelect = (id: string) => {
    let selectedItems = [...state.selectedItems];
    let index = selectedItems.findIndex((e) => e === id);
    if (index === -1) {
      selectedItems.push(id);
    } else {
      selectedItems.splice(index, 1);
    }
    let isAllChecked = true;
    state.accReading.forEach((ele: any) => {
      if (
        ele.status === "submitted" &&
        selectedItems.includes(ele._id as string) === false
      ) {
        isAllChecked = false;
      }
    });
    setState({ ...state, selectedItems, isAllChecked });
  };

  const handleAcceptOrReject = (id: string, isAccept: boolean) => {
    showConfirmDialog(
      "Are You Sure",
      `Do you want to ${
        isAccept ? "Accept" : "Reject"
      } the Air Cooled Chiller reading`,
      async () => {
        setState({
          ...state,
          isLoading: true,
          manageActionsCallout: {
            isOpen: false,
            element: undefined,
            data: undefined,
          },
        });
        let obj: IReadingStatus = {
          ids: [id],
          status: isAccept ? "approved" : "rejected",
        };
        let result = await updateAccReadingStatus(obj);
        if (result.success) {
          let accReading = [...state.accReading];
          let index = accReading.findIndex((ele) => ele._id === id);
          if (index !== -1) {
            accReading[index].status = isAccept ? "approved" : "rejected";
          }
          showToast(
            `Air-Cooled-Chiller Reading ${
              isAccept ? "approved" : "rejected"
            } successfully`,
            "success"
          );
          let selectedItems = [...state.selectedItems];
          if (state.isAllChecked) {
            let indx = selectedItems.findIndex((e) => e === id);
            selectedItems.splice(indx, 1);
          }
          setState({
            ...state,
            accReading,
            selectedItems,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              element: undefined,
              data: undefined,
            },
          });
        } else {
          showToast(
            result?.message ||
              `An error occurred while ${
                isAccept ? "approving" : "rejecting"
              } a Air Cooled Chiller reading`,
            "error"
          );
          setState({
            ...state,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              element: undefined,
              data: undefined,
            },
          });
        }
      }
    );
  };

  const handleDialogOpen = (isOpen: boolean, id?: string) => {
    let accReading = undefined;
    let index = undefined;
    if (id !== undefined) {
      index = state.accReading.findIndex((ele) => ele._id === id);
      accReading = state.accReading[index];
    }
    setState({
      ...state,
      manageAccDialog: {
        isOpen: isOpen,
        index: index,
        data: accReading,
      },
    });
  };

  const handleDialogClose = (data: IAirCooledChillerReading) => {
    setState({ ...state, isLoading: true });
    let accReading = [...state.accReading];
    if (data) {
      if (state.manageAccDialog.index !== undefined) {
        accReading[state.manageAccDialog.index] = data;
      }
    }
    setState({
      ...state,
      isLoading: false,
      manageAccDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
      accReading,
    });
  };

  const handleRejectAllOpenDialog = (ids: string[]) => {
    setState({
      ...state,
      manageRejectDialog: {
        isOpen: true,
        ids: ids || [],
        remarks: "",
      },
    });
  };
  const handleRejectOpenDialog = (id: string, remarks?: string) => {
    setState({
      ...state,
      manageRejectDialog: {
        isOpen: true,
        ids: [id],
        remarks: remarks || "",
      },
    });
  };

  const handleRejectCloseDialog = async (
    isSuccess: boolean,
    remarks?: string
  ) => {
    let accReading = [...state.accReading];
    let selectedItems: string[] = state.manageRejectDialog.ids;
    if (isSuccess) {
      setState({
        ...state,
        isLoading: true,
      });
      let obj: IReadingStatus = {
        ids: selectedItems,
        status: "rejected",
        remarks: remarks || "",
      };
      let result = await updateAccReadingStatus(obj);
      if (result?.success) {
        selectedItems.forEach((item) => {
          let index = accReading.findIndex((ele) => ele._id === item);
          if (index !== -1) {
            accReading[index].status = "rejected";
            accReading[index].remarks = remarks || "";
          }
        });
        showToast(
          `Air Cooled Chiller Reading rejected successfully`,
          "success"
        );
        setState({
          ...state,
          accReading,
          selectedItems: [],
          manageRejectDialog: {
            isOpen: false,
            ids: [],
            remarks: undefined,
          },
          isLoading: false,
        });
      } else {
        showToast(
          result?.message ||
            `An error occurred while rejecting a air cooled chiller reading`,
          "error"
        );
        setState({
          ...state,
          manageRejectDialog: {
            isOpen: false,
            ids: [],
            remarks: undefined,
          },
          isLoading: false,
        });
      }
    } else {
      setState({
        ...state,
        manageRejectDialog: {
          isOpen: false,
          ids: [],
          remarks: undefined,
        },
        accReading,
      });
    }
  };

  const handleLoading = () => {
    setState({
      ...state,
      isLoading: !state.isLoading,
    });
  };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    data?: IAirCooledChillerReading
  ) => {
    setState({
      ...state,
      manageActionsCallout: {
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        data,
      },
    });
  };

  return (
    <React.Fragment>
      <Stack horizontal horizontalAlign="end">
        <DefaultButton
          text="Accept"
          disabled={state.selectedItems.length === 0}
          onClick={() => handleAcceptOrRejectSelected(true)}
          className="custom-secondary-btn"
        />
        &nbsp;&nbsp;
        <DefaultButton
          text="Reject"
          disabled={state.selectedItems.length === 0}
          onClick={() => handleAcceptOrRejectSelected(false)}
          className="custom-secondary-btn"
        />
      </Stack>
      <br />
      <Stack>
        <div className="office-table bg-white">
          <table className="ms-Table bordered-table w-100">
            <thead className="html-table-thead">
              <tr>
                <th rowSpan={2}>
                  <Checkbox
                    checked={state.isAllChecked}
                    onChange={(eve, checked) => onSelectAll(checked as boolean)}
                  />
                </th>
                <th rowSpan={2}>Time (Hrs)</th>
                <th rowSpan={2}>System</th>
                <th colSpan={14}>EVAPORATOR</th>
                <th colSpan={14}>CONDENSER</th>
                <th colSpan={24}>COMPRESSOR</th>
                <th colSpan={18}>MOTOR VSD</th>
                <th colSpan={5}>FAN</th>
                <th colSpan={6}>FLASH TANK</th>
                <th rowSpan={2}>Shift Tech Sign</th>
                <th rowSpan={2}>Status</th>
                <th rowSpan={2}>Remarks</th>
                <th rowSpan={2}>Read At</th>
                <th rowSpan={2}>Actions</th>
              </tr>
              <tr>
                <th>Active CHW Setpoint</th>
                <th>CHW Inlet/Entering Temp</th>
                <th>CHW Outlet/Leaving Temp</th>
                <th>Refrigerant Pressure CKT 01</th>
                <th>Refrigerant Pressure CKT 02</th>

                <th>Suction Eductor Temp CKT 01</th>
                <th>Suction Eductor Temp CKT 02</th>
                <th>CHW Inlet Pressure</th>
                <th>CHW Outlet Pressure</th>

                <th>Saturated Refrigerant Temp CKT 01</th>
                <th>Saturated Refrigerant Temp CKT 02</th>
                <th>Approach Temp CKT 01</th>
                <th>Approach Temp CKT 02</th>
                <th>Outdoor Air Temperature</th>

                <th>Discharge Pressure CKT 01</th>
                <th>Discharge Pressure CKT 02</th>
                <th>Discharge Temp CKT 01</th>
                <th>Discharge Temp CKT 02</th>
                <th>Refrigerent Saturated Temp CKT 01</th>
                <th>Refrigerent Saturated Temp CKT 02</th>

                <th>Sub Cooling Temp CKT 01</th>
                <th>Sub Cooling Temp CKT 02</th>
                <th>Oil Pressure CKT 01</th>
                <th>Oil Pressure CKT 02</th>
                <th>Discharge Super Heat CKT 01</th>
                <th>Discharge Super Heat CKT 02</th>

                <th>Sub Cooling CKT 01</th>
                <th>Sub Cooling CKT 02</th>

                <th>Starts CKT 01</th>
                <th>Starts CKT 02</th>

                <th>Running Time CKT 01</th>
                <th>Running Time CKT 02</th>
                <th>Speed CKT 01</th>
                <th>Speed CKT 02</th>
                <th>Oil Level Above Bottom Sight Glass CKT 01</th>
                <th>Oil Level Above Bottom Sight Glass CKT 02</th>

                <th>Amp CKT 01</th>
                <th>Amp CKT 02</th>
                <th>Percentage RLA CKT 01</th>
                <th>Percentage RLA CKT 02</th>
                <th>Intermediate Oil Pressure CKT 01</th>
                <th>Intermediate Oil Pressure CKT 02</th>
                <th>Unit Volts CKT 01</th>
                <th>Unit Volts CKT 02</th>
                <th>Oil Temp CKT 01</th>
                <th>Oil Temp CKT 02</th>

                <th>Discharge Temp CKT 01</th>
                <th>Discharge Temp CKT 02</th>
                <th>Saturation Discharge Temp CKT 01</th>
                <th>Saturation Discharge Temp CKT 02</th>
                <th>Discharge Super Heat CKT 01 </th>
                <th>Discharge Super Heat CKT 02</th>

                <th>Dc Bus Voltage CKT 01</th>
                <th>Dc Bus Voltage CKT 02</th>
                <th>VSD Command</th>
                <th>VSD Actual</th>
                <th>Motor Current CKT 01</th>
                <th>Motor Current CKT 02</th>
                <th>IGBT Base Plate Temp CKT 01</th>
                <th>IGBT Base Plate Temp CKT 02</th>
                <th>Compressor 01 KW</th>
                <th>Compressor 02 KW</th>

                <th>Fla CKT 01</th>
                <th>Fla CKT 02</th>
                <th>Command Freq CKT 01</th>
                <th>Command Freq CKT 02</th>
                <th>Actual Freq CKT 01</th>
                <th>Actual Freq CKT 02</th>
                <th>Internal Ambient Temp CKT 01</th>
                <th>Internal Ambient Temp CKT 02</th>

                <th>VFD Fan Speed CKT 01</th>
                <th>VFD Fan Speed CKT 02</th>
                <th>Fan KW CKT 01</th>
                <th>Fan KW CKT 02</th>
                <th>Ambient Temp</th>

                <th>Economizer Value Open CKT 01</th>
                <th>Economizer Value Open CKT 02</th>
                <th>Condenser Drain Valve Open CKT 01</th>
                <th>Condenser Drain Valve Open CKT 02</th>
                <th>Flash Tank Level CKT 01</th>
                <th>Flash Tank Level CKT 02</th>
              </tr>
            </thead>
            <tbody>
              {EVEN_SHIFT.map((ele, index) => {
                let item = state.accReading.find((e) => e.shiftTime === ele);
                return (
                  <tr key={index}>
                    <th>
                      <Checkbox
                        disabled={!item || item.status !== "submitted"}
                        checked={state.selectedItems.includes(
                          item?._id as string
                        )}
                        onChange={() => onIndividualSelect(item?._id as string)}
                        className="ml-12"
                      />
                    </th>
                    <th>{ele}</th>
                    <td>
                      {item?.isSystemOn !== undefined
                        ? item?.isSystemOn === true
                          ? "On"
                          : "Off"
                        : ""}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.activeChwSetPoint || "",
                          "evaporator.activeChwSetPoint",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.activeChwSetPoint || ""}
                      {isInThreshold(
                        item?.evaporator?.activeChwSetPoint || "",
                        "evaporator.activeChwSetPoint",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.activeChwSetPoint || "",
                              "evaporator.activeChwSetPoint",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.chwInletTemperature || "",
                          "evaporator.chwInletTemperature",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.chwInletTemperature || ""}
                      {isInThreshold(
                        item?.evaporator?.chwInletTemperature || "",
                        "evaporator.chwInletTemperature",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.chwInletTemperature || "",
                              "evaporator.chwInletTemperature",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.chwOutletTemperature || "",
                          "evaporator.chwOutletTemperature",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.chwOutletTemperature || ""}
                      {isInThreshold(
                        item?.evaporator?.chwOutletTemperature || "",
                        "evaporator.chwOutletTemperature",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.chwOutletTemperature || "",
                              "evaporator.chwOutletTemperature",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.refrigerantPressureCkt01 || "",
                          "evaporator.refrigerantPressureCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.refrigerantPressureCkt01 || ""}
                      {isInThreshold(
                        item?.evaporator?.refrigerantPressureCkt01 || "",
                        "evaporator.refrigerantPressureCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.refrigerantPressureCkt01 || "",
                              "evaporator.refrigerantPressureCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.refrigerantPressureCkt02 || "",
                          "evaporator.refrigerantPressureCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.refrigerantPressureCkt02 || ""}
                      {isInThreshold(
                        item?.evaporator?.refrigerantPressureCkt02 || "",
                        "evaporator.refrigerantPressureCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.refrigerantPressureCkt02 || "",
                              "evaporator.refrigerantPressureCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.suctionEductorTempCkt01 || "",
                          "evaporator.suctionEductorTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.suctionEductorTempCkt01 || ""}
                      {isInThreshold(
                        item?.evaporator?.suctionEductorTempCkt01 || "",
                        "evaporator.suctionEductorTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.suctionEductorTempCkt01 || "",
                              "evaporator.suctionEductorTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.suctionEductorTempCkt02 || "",
                          "evaporator.suctionEductorTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.suctionEductorTempCkt02 || ""}
                      {isInThreshold(
                        item?.evaporator?.suctionEductorTempCkt02 || "",
                        "evaporator.suctionEductorTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.suctionEductorTempCkt02 || "",
                              "evaporator.suctionEductorTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.chwInletPressure || "",
                          "evaporator.chwInletPressure",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.chwInletPressure || ""}
                      {isInThreshold(
                        item?.evaporator?.chwInletPressure || "",
                        "evaporator.chwInletPressure",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.chwInletPressure || "",
                              "evaporator.chwInletPressure",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.chwOutletPressure || "",
                          "evaporator.chwOutletPressure",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.chwOutletPressure || ""}
                      {isInThreshold(
                        item?.evaporator?.chwOutletPressure || "",
                        "evaporator.chwOutletPressure",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.chwOutletPressure || "",
                              "evaporator.chwOutletPressure",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator
                            ?.saturatedEvaporatorRefrigerantTempCkt01 || "",
                          "evaporator.saturatedEvaporatorRefrigerantTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator
                        ?.saturatedEvaporatorRefrigerantTempCkt01 || ""}
                      {isInThreshold(
                        item?.evaporator
                          ?.saturatedEvaporatorRefrigerantTempCkt01 || "",
                        "evaporator.saturatedEvaporatorRefrigerantTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator
                                ?.saturatedEvaporatorRefrigerantTempCkt01 || "",
                              "evaporator.saturatedEvaporatorRefrigerantTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator
                            ?.saturatedEvaporatorRefrigerantTempCkt02 || "",
                          "evaporator.saturatedEvaporatorRefrigerantTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator
                        ?.saturatedEvaporatorRefrigerantTempCkt02 || ""}
                      {isInThreshold(
                        item?.evaporator
                          ?.saturatedEvaporatorRefrigerantTempCkt02 || "",
                        "evaporator.saturatedEvaporatorRefrigerantTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator
                                ?.saturatedEvaporatorRefrigerantTempCkt02 || "",
                              "evaporator.saturatedEvaporatorRefrigerantTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.approachTempCkt01 || "",
                          "evaporator.approachTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.approachTempCkt01 || ""}
                      {isInThreshold(
                        item?.evaporator?.approachTempCkt01 || "",
                        "evaporator.approachTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.approachTempCkt01 || "",
                              "evaporator.approachTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.approachTempCkt02 || "",
                          "evaporator.approachTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.approachTempCkt02 || ""}
                      {isInThreshold(
                        item?.evaporator?.approachTempCkt02 || "",
                        "evaporator.approachTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.approachTempCkt02 || "",
                              "evaporator.approachTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.outdoorAirTemperature || "",
                          "evaporator.outdoorAirTemperature",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.outdoorAirTemperature || ""}
                      {isInThreshold(
                        item?.evaporator?.outdoorAirTemperature || "",
                        "evaporator.outdoorAirTemperature",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.outdoorAirTemperature || "",
                              "evaporator.outdoorAirTemperature",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.dischargePressureCkt01 || "",
                          "condenser.dischargePressureCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.dischargePressureCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.dischargePressureCkt01 || "",
                        "condenser.dischargePressureCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.dischargePressureCkt01 || "",
                              "condenser.dischargePressureCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.dischargePressureCkt02 || "",
                          "condenser.dischargePressureCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.dischargePressureCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.dischargePressureCkt02 || "",
                        "condenser.dischargePressureCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.dischargePressureCkt02 || "",
                              "condenser.dischargePressureCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.dischargeTempCkt01 || "",
                          "condenser.dischargeTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.dischargeTempCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.dischargeTempCkt01 || "",
                        "condenser.dischargeTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.dischargeTempCkt01 || "",
                              "condenser.dischargeTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.dischargeTempCkt02 || "",
                          "condenser.dischargeTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.dischargeTempCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.dischargeTempCkt02 || "",
                        "condenser.dischargeTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.dischargeTempCkt02 || "",
                              "condenser.dischargeTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.refrigerantSaturatedTempCkt01 || "",
                          "condenser.refrigerantSaturatedTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.refrigerantSaturatedTempCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.refrigerantSaturatedTempCkt01 || "",
                        "condenser.refrigerantSaturatedTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.refrigerantSaturatedTempCkt01 ||
                                "",
                              "condenser.refrigerantSaturatedTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.refrigerantSaturatedTempCkt02 || "",
                          "condenser.refrigerantSaturatedTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.refrigerantSaturatedTempCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.refrigerantSaturatedTempCkt02 || "",
                        "condenser.refrigerantSaturatedTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.refrigerantSaturatedTempCkt02 ||
                                "",
                              "condenser.refrigerantSaturatedTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.subCoolingTempCkt01 || "",
                          "condenser.subCoolingTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.subCoolingTempCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.subCoolingTempCkt01 || "",
                        "condenser.subCoolingTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.subCoolingTempCkt01 || "",
                              "condenser.subCoolingTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.subCoolingTempCkt02 || "",
                          "condenser.subCoolingTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.subCoolingTempCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.subCoolingTempCkt02 || "",
                        "condenser.subCoolingTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.subCoolingTempCkt02 || "",
                              "condenser.subCoolingTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.oilPressureCkt01 || "",
                          "condenser.oilPressureCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.oilPressureCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.oilPressureCkt01 || "",
                        "condenser.oilPressureCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.oilPressureCkt01 || "",
                              "condenser.oilPressureCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.oilPressureCkt02 || "",
                          "condenser.oilPressureCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.oilPressureCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.oilPressureCkt02 || "",
                        "condenser.oilPressureCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.oilPressureCkt02 || "",
                              "condenser.oilPressureCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.dischargeSuperHeatCkt01 || "",
                          "condenser.dischargeSuperHeatCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.dischargeSuperHeatCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.dischargeSuperHeatCkt01 || "",
                        "condenser.dischargeSuperHeatCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.dischargeSuperHeatCkt01 || "",
                              "condenser.dischargeSuperHeatCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.dischargeSuperHeatCkt02 || "",
                          "condenser.dischargeSuperHeatCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.dischargeSuperHeatCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.dischargeSuperHeatCkt02 || "",
                        "condenser.dischargeSuperHeatCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.dischargeSuperHeatCkt02 || "",
                              "condenser.dischargeSuperHeatCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    {/* PNQ-DC2 */}
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.subCoolingCkt01 || "",
                          "condenser.subCoolingCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.subCoolingCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.subCoolingCkt01 || "",
                        "condenser.subCoolingCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.subCoolingCkt01 || "",
                              "condenser.subCoolingCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.subCoolingCkt02 || "",
                          "condenser.subCoolingCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.subCoolingCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.subCoolingCkt02 || "",
                        "condenser.subCoolingCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.subCoolingCkt02 || "",
                              "condenser.subCoolingCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>

                    <td>
                      {item?.compressor?.startCkt01
                        ? moment(item?.compressor?.startCkt01).format(
                            "HH:mm DD-MM-YYYY"
                          )
                        : ""}
                    </td>
                    <td>
                      {item?.compressor?.startCkt02
                        ? moment(item?.compressor?.startCkt02).format(
                            "HH:mm DD-MM-YYYY"
                          )
                        : ""}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.runningTimeCkt01 || "",
                          "compressor.runningTimeCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.runningTimeCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.runningTimeCkt01 || "",
                        "compressor.runningTimeCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.runningTimeCkt01 || "",
                              "compressor.runningTimeCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.runningTimeCkt02 || "",
                          "compressor.runningTimeCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.runningTimeCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.runningTimeCkt02 || "",
                        "compressor.runningTimeCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.runningTimeCkt02 || "",
                              "compressor.runningTimeCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.speedCkt01 || "",
                          "compressor.speedCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.speedCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.speedCkt01 || "",
                        "compressor.speedCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.speedCkt01 || "",
                              "compressor.speedCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.speedCkt02 || "",
                          "compressor.speedCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.speedCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.speedCkt02 || "",
                        "compressor.speedCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.speedCkt02 || "",
                              "compressor.speedCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td>
                      {item?.compressor
                        ?.isOilLevelAboveBottomSightGlassCkt01 !== undefined
                        ? item.compressor
                            .isOilLevelAboveBottomSightGlassCkt01 === true
                          ? "Yes"
                          : "No"
                        : ""}
                    </td>
                    <td>
                      {item?.compressor
                        ?.isOilLevelAboveBottomSightGlassCkt02 !== undefined
                        ? item.compressor.isOilLevelAboveBottomSightGlassCkt02
                          ? "Yes"
                          : "No"
                        : ""}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.ampCkt01 || "",
                          "compressor.ampCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.ampCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.ampCkt01 || "",
                        "compressor.ampCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.ampCkt01 || "",
                              "compressor.ampCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.ampCkt02 || "",
                          "compressor.ampCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.ampCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.ampCkt02 || "",
                        "compressor.ampCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.ampCkt02 || "",
                              "compressor.ampCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.percentageRlaCkt01 || "",
                          "compressor.percentageRlaCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.percentageRlaCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.percentageRlaCkt01 || "",
                        "compressor.percentageRlaCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.percentageRlaCkt01 || "",
                              "compressor.percentageRlaCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.percentageRlaCkt02 || "",
                          "compressor.percentageRlaCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.percentageRlaCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.percentageRlaCkt02 || "",
                        "compressor.percentageRlaCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.percentageRlaCkt02 || "",
                              "compressor.percentageRlaCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.intermediateOilPressureCkt01 || "",
                          "compressor.intermediateOilPressureCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.intermediateOilPressureCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.intermediateOilPressureCkt01 || "",
                        "compressor.intermediateOilPressureCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.intermediateOilPressureCkt01 ||
                                "",
                              "compressor.intermediateOilPressureCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.intermediateOilPressureCkt02 || "",
                          "compressor.intermediateOilPressureCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.intermediateOilPressureCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.intermediateOilPressureCkt02 || "",
                        "compressor.intermediateOilPressureCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.intermediateOilPressureCkt02 ||
                                "",
                              "compressor.intermediateOilPressureCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.unitVoltsCkt01 || "",
                          "compressor.unitVoltsCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.unitVoltsCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.unitVoltsCkt01 || "",
                        "compressor.unitVoltsCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.unitVoltsCkt01 || "",
                              "compressor.unitVoltsCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.unitVoltsCkt02 || "",
                          "compressor.unitVoltsCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.unitVoltsCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.unitVoltsCkt02 || "",
                        "compressor.unitVoltsCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.unitVoltsCkt02 || "",
                              "compressor.unitVoltsCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilTempCkt01 || "",
                          "compressor.oilTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilTempCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.oilTempCkt01 || "",
                        "compressor.oilTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilTempCkt01 || "",
                              "compressor.oilTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilTempCkt02 || "",
                          "compressor.oilTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilTempCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.oilTempCkt02 || "",
                        "compressor.oilTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilTempCkt02 || "",
                              "compressor.oilTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    {/* PNQ-Dc2 */}
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.dischargeTempCkt01 || "",
                          "compressor.dischargeTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.dischargeTempCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.dischargeTempCkt01 || "",
                        "compressor.dischargeTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.dischargeTempCkt01 || "",
                              "compressor.dischargeTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.dischargeTempCkt02 || "",
                          "compressor.dischargeTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.dischargeTempCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.dischargeTempCkt02 || "",
                        "compressor.dischargeTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.dischargeTempCkt02 || "",
                              "compressor.dischargeTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.saturationDischargeTempCkt01 || "",
                          "compressor.saturationDischargeTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.saturationDischargeTempCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.saturationDischargeTempCkt01 || "",
                        "compressor.saturationDischargeTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.saturationDischargeTempCkt01 ||
                                "",
                              "compressor.saturationDischargeTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.saturationDischargeTempCkt02 || "",
                          "compressor.saturationDischargeTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.saturationDischargeTempCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.saturationDischargeTempCkt02 || "",
                        "compressor.saturationDischargeTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.saturationDischargeTempCkt02 ||
                                "",
                              "compressor.saturationDischargeTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.dischargeSuperHeatCkt01 || "",
                          "compressor.dischargeSuperHeatCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.dischargeSuperHeatCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.dischargeSuperHeatCkt01 || "",
                        "compressor.dischargeSuperHeatCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.dischargeSuperHeatCkt01 || "",
                              "compressor.dischargeSuperHeatCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.dischargeSuperHeatCkt02 || "",
                          "compressor.dischargeSuperHeatCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.dischargeSuperHeatCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.dischargeSuperHeatCkt02 || "",
                        "compressor.dischargeSuperHeatCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.dischargeSuperHeatCkt02 || "",
                              "compressor.dischargeSuperHeatCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>

                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.dcBusVoltageCkt01 || "",
                          "motorVSD.dcBusVoltageCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.dcBusVoltageCkt01 || ""}
                      {isInThreshold(
                        item?.motorVSD?.dcBusVoltageCkt01 || "",
                        "motorVSD.dcBusVoltageCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.dcBusVoltageCkt01 || "",
                              "motorVSD.dcBusVoltageCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.dcBusVoltageCkt02 || "",
                          "motorVSD.dcBusVoltageCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.dcBusVoltageCkt02 || ""}
                      {isInThreshold(
                        item?.motorVSD?.dcBusVoltageCkt02 || "",
                        "motorVSD.dcBusVoltageCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.dcBusVoltageCkt02 || "",
                              "motorVSD.dcBusVoltageCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.vsdCommand || "",
                          "motorVSD.vsdCommand",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.vsdCommand || ""}
                      {isInThreshold(
                        item?.motorVSD?.vsdCommand || "",
                        "motorVSD.vsdCommand",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.vsdCommand || "",
                              "motorVSD.vsdCommand",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.vsdActual || "",
                          "motorVSD.vsdActual",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.vsdActual || ""}
                      {isInThreshold(
                        item?.motorVSD?.vsdActual || "",
                        "motorVSD.vsdActual",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.vsdActual || "",
                              "motorVSD.vsdActual",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.motorCurrentCkt01 || "",
                          "motorVSD.motorCurrentCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.motorCurrentCkt01 || ""}
                      {isInThreshold(
                        item?.motorVSD?.motorCurrentCkt01 || "",
                        "motorVSD.motorCurrentCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.motorCurrentCkt01 || "",
                              "motorVSD.motorCurrentCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.motorCurrentCkt02 || "",
                          "motorVSD.motorCurrentCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.motorCurrentCkt02 || ""}
                      {isInThreshold(
                        item?.motorVSD?.motorCurrentCkt02 || "",
                        "motorVSD.motorCurrentCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.motorCurrentCkt02 || "",
                              "motorVSD.motorCurrentCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.IGBTBasePlateTempCkt01 || "",
                          "motorVSD.IGBTBasePlateTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.IGBTBasePlateTempCkt01 || ""}
                      {isInThreshold(
                        item?.motorVSD?.IGBTBasePlateTempCkt01 || "",
                        "motorVSD.IGBTBasePlateTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.IGBTBasePlateTempCkt01 || "",
                              "motorVSD.IGBTBasePlateTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.IGBTBasePlateTempCkt02 || "",
                          "motorVSD.IGBTBasePlateTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.IGBTBasePlateTempCkt02 || ""}
                      {isInThreshold(
                        item?.motorVSD?.IGBTBasePlateTempCkt02 || "",
                        "motorVSD.IGBTBasePlateTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.IGBTBasePlateTempCkt02 || "",
                              "motorVSD.IGBTBasePlateTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.compressor01kw || "",
                          "motorVSD.compressor01kw",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.compressor01kw || ""}
                      {isInThreshold(
                        item?.motorVSD?.compressor01kw || "",
                        "motorVSD.compressor01kw",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.compressor01kw || "",
                              "motorVSD.compressor01kw",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.compressor02kw || "",
                          "motorVSD.compressor02kw",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.compressor02kw || ""}
                      {isInThreshold(
                        item?.motorVSD?.compressor02kw || "",
                        "motorVSD.compressor02kw",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.compressor02kw || "",
                              "motorVSD.compressor02kw",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    {/* PNQ-DC2 */}
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.flaCkt01 || "",
                          "motorVSD.flaCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.flaCkt01 || ""}
                      {isInThreshold(
                        item?.motorVSD?.flaCkt01 || "",
                        "motorVSD.flaCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.flaCkt01 || "",
                              "motorVSD.flaCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.flaCkt02 || "",
                          "motorVSD.flaCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.flaCkt02 || ""}
                      {isInThreshold(
                        item?.motorVSD?.flaCkt02 || "",
                        "motorVSD.flaCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.flaCkt02 || "",
                              "motorVSD.flaCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.commandFreqCkt01 || "",
                          "motorVSD.commandFreqCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.commandFreqCkt01 || ""}
                      {isInThreshold(
                        item?.motorVSD?.commandFreqCkt01 || "",
                        "motorVSD.commandFreqCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.commandFreqCkt01 || "",
                              "motorVSD.commandFreqCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.commandFreqCkt02 || "",
                          "motorVSD.commandFreqCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.commandFreqCkt02 || ""}
                      {isInThreshold(
                        item?.motorVSD?.commandFreqCkt02 || "",
                        "motorVSD.commandFreqCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.commandFreqCkt02 || "",
                              "motorVSD.commandFreqCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.actualFreqCkt01 || "",
                          "motorVSD.actualFreqCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.actualFreqCkt01 || ""}
                      {isInThreshold(
                        item?.motorVSD?.actualFreqCkt01 || "",
                        "motorVSD.actualFreqCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.actualFreqCkt01 || "",
                              "motorVSD.actualFreqCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.actualFreqCkt02 || "",
                          "motorVSD.actualFreqCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.actualFreqCkt02 || ""}
                      {isInThreshold(
                        item?.motorVSD?.actualFreqCkt02 || "",
                        "motorVSD.actualFreqCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.actualFreqCkt02 || "",
                              "motorVSD.actualFreqCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.internalAmbientTempCkt01 || "",
                          "motorVSD.internalAmbientTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.internalAmbientTempCkt01 || ""}
                      {isInThreshold(
                        item?.motorVSD?.internalAmbientTempCkt01 || "",
                        "motorVSD.internalAmbientTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.internalAmbientTempCkt01 || "",
                              "motorVSD.internalAmbientTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motorVSD?.internalAmbientTempCkt02 || "",
                          "motorVSD.internalAmbientTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motorVSD?.internalAmbientTempCkt02 || ""}
                      {isInThreshold(
                        item?.motorVSD?.internalAmbientTempCkt02 || "",
                        "motorVSD.internalAmbientTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motorVSD?.internalAmbientTempCkt02 || "",
                              "motorVSD.internalAmbientTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.fan?.vfdFanSpeedCkt01 || "",
                          "fan.vfdFanSpeedCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.fan?.vfdFanSpeedCkt01 || ""}
                      {isInThreshold(
                        item?.fan?.vfdFanSpeedCkt01 || "",
                        "fan.vfdFanSpeedCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.fan?.vfdFanSpeedCkt01 || "",
                              "fan.vfdFanSpeedCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.fan?.vfdFanSpeedCkt02 || "",
                          "fan.vfdFanSpeedCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.fan?.vfdFanSpeedCkt02 || ""}
                      {isInThreshold(
                        item?.fan?.vfdFanSpeedCkt02 || "",
                        "fan.vfdFanSpeedCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.fan?.vfdFanSpeedCkt02 || "",
                              "fan.vfdFanSpeedCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.fan?.fanKwtCkt01 || "",
                          "fan.fanKwtCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.fan?.fanKwtCkt01 || ""}
                      {isInThreshold(
                        item?.fan?.fanKwtCkt01 || "",
                        "fan.fanKwtCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.fan?.fanKwtCkt01 || "",
                              "fan.fanKwtCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.fan?.fanKwtCkt02 || "",
                          "fan.fanKwtCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.fan?.fanKwtCkt02 || ""}
                      {isInThreshold(
                        item?.fan?.fanKwtCkt02 || "",
                        "fan.fanKwtCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.fan?.fanKwtCkt02 || "",
                              "fan.fanKwtCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.fan?.ambientTemp || "",
                          "fan.ambientTemp",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.fan?.ambientTemp || ""}
                      {isInThreshold(
                        item?.fan?.ambientTemp || "",
                        "fan.ambientTemp",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.fan?.ambientTemp || "",
                              "fan.ambientTemp",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    {/* PNQ-DC2 */}
                    <td
                      className={
                        isInThreshold(
                          item?.flashTank?.economizerValueOpenCkt01 || "",
                          "flashTank.economizerValueOpenCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.flashTank?.economizerValueOpenCkt01 || ""}
                      {isInThreshold(
                        item?.flashTank?.economizerValueOpenCkt01 || "",
                        "flashTank.economizerValueOpenCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.flashTank?.economizerValueOpenCkt01 || "",
                              "flashTank.economizerValueOpenCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.flashTank?.economizerValueOpenCkt02 || "",
                          "flashTank.economizerValueOpenCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.flashTank?.economizerValueOpenCkt02 || ""}
                      {isInThreshold(
                        item?.flashTank?.economizerValueOpenCkt02 || "",
                        "flashTank.economizerValueOpenCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.flashTank?.economizerValueOpenCkt02 || "",
                              "flashTank.economizerValueOpenCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.flashTank?.condenserDrainValveOpenCkt01 || "",
                          "flashTank.condenserDrainValveOpenCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.flashTank?.condenserDrainValveOpenCkt01 || ""}
                      {isInThreshold(
                        item?.flashTank?.condenserDrainValveOpenCkt01 || "",
                        "flashTank.condenserDrainValveOpenCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.flashTank?.condenserDrainValveOpenCkt01 ||
                                "",
                              "flashTank.condenserDrainValveOpenCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.flashTank?.condenserDrainValveOpenCkt02 || "",
                          "flashTank.condenserDrainValveOpenCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.flashTank?.condenserDrainValveOpenCkt02 || ""}
                      {isInThreshold(
                        item?.flashTank?.condenserDrainValveOpenCkt02 || "",
                        "flashTank.condenserDrainValveOpenCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.flashTank?.condenserDrainValveOpenCkt02 ||
                                "",
                              "flashTank.condenserDrainValveOpenCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.flashTank?.flashTankLevelCkt01 || "",
                          "flashTank.flashTankLevelCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.flashTank?.flashTankLevelCkt01 || ""}
                      {isInThreshold(
                        item?.flashTank?.flashTankLevelCkt01 || "",
                        "flashTank.flashTankLevelCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.flashTank?.flashTankLevelCkt01 || "",
                              "flashTank.flashTankLevelCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.flashTank?.flashTankLevelCkt02 || "",
                          "flashTank.flashTankLevelCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.flashTank?.flashTankLevelCkt02 || ""}
                      {isInThreshold(
                        item?.flashTank?.flashTankLevelCkt02 || "",
                        "flashTank.flashTankLevelCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.flashTank?.flashTankLevelCkt02 || "",
                              "flashTank.flashTankLevelCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td>{item?.recordedBy || ""}</td>
                    <td>
                      {item && item.status ? UPS_STATUS[item.status] : ""}
                    </td>
                    <td>{item?.remarks || ""}</td>
                    <td>
                      {item?.date
                        ? moment(item.date).format(READ_AT_TIME_FORMAT)
                        : ""}
                    </td>
                    <td align="center" className="no-wrap">
                      {item && item.status === "submitted" && (
                        <span
                          onClick={(ele) =>
                            handleCallOutOpenClose(true, ele, item)
                          }
                        >
                          <Icon
                            path={mdiDotsVertical}
                            size={0.9}
                            className="pointer"
                            color="#135c8a"
                          />
                        </span>
                        // <React.Fragment>
                        //   <TooltipHost
                        //     content="Update"
                        //     calloutProps={{ gapSpace: 0 }}
                        //   >
                        //     <span
                        //       onClick={() =>
                        //         handleDialogOpen(true, item?._id as string)
                        //       }
                        //     >
                        //       <Icon
                        //         path={mdiPencil}
                        //         size={0.7}
                        //         className="pointer"
                        //       />
                        //     </span>
                        //   </TooltipHost>
                        //   &nbsp;&nbsp;
                        //   <TooltipHost
                        //     content="Accept"
                        //     calloutProps={{ gapSpace: 0 }}
                        //   >
                        //     <span
                        //       onClick={() =>
                        //         handleAcceptOrReject(item?._id as string, true)
                        //       }
                        //     >
                        //       <Icon
                        //         path={mdiCheck}
                        //         size={0.8}
                        //         className="pointer"
                        //         color="green"
                        //       />
                        //     </span>
                        //   </TooltipHost>
                        //   &nbsp;&nbsp;
                        //   <TooltipHost
                        //     content="Reject"
                        //     calloutProps={{ gapSpace: 0 }}
                        //   >
                        //     <span
                        //       onClick={() =>
                        //         handleRejectOpenDialog(
                        //           item?._id as string,
                        //           item?.remarks
                        //         )
                        //       }
                        //     >
                        //       <Icon
                        //         path={mdiClose}
                        //         size={0.8}
                        //         className="pointer"
                        //         color="red"
                        //       />
                        //     </span>
                        //   </TooltipHost>
                        // </React.Fragment>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Stack>
      {state.manageActionsCallout.isOpen && (
        <Callout
          styles={{ root: { width: 95, padding: 20 } }}
          role="alertdialog"
          ariaLabelledBy={
            state.manageActionsCallout && state.manageActionsCallout.data?._id
          }
          gapSpace={0}
          target={state.manageActionsCallout.element}
          directionalHint={DirectionalHint.leftCenter}
          onDismiss={() => handleCallOutOpenClose(false)}
          // setInitialFocus={true}
        >
          <Stack tokens={childrenGap8}>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleDialogOpen(
                    true,
                    state.manageActionsCallout.data?._id as string
                  )
                }
                className="callout-options"
              >
                Edit
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleAcceptOrReject(
                    state.manageActionsCallout.data?._id as string,
                    true
                  )
                }
                className="callout-options"
              >
                Accept
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleRejectOpenDialog(
                    state.manageActionsCallout.data?._id as string,
                    state.manageActionsCallout.data?.remarks
                  )
                }
                className="callout-options"
              >
                Reject
              </span>
            </Stack.Item>
          </Stack>
        </Callout>
      )}
      {state.manageAccDialog.isOpen && (
        <ManageAccDialog
          data={state.manageAccDialog.data}
          handleLoading={handleLoading}
          handleDialogClose={(data: IAirCooledChillerReading) =>
            handleDialogClose(data)
          }
          threshold={threshold}
        />
      )}
      {state.manageRejectDialog.isOpen && (
        <ManageAllRejectDialog
          handleRejectCloseDialog={(isSuccess: boolean, remarks?: string) =>
            handleRejectCloseDialog(isSuccess, remarks)
          }
          remarks={state.manageRejectDialog.remarks}
          subText="air cooled chiller reading"
        />
      )}
    </React.Fragment>
  );
};

export default AccTable;
